'use client';

import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import {
  faCircleDollar,
  faBriefcaseMedical,
  faHospital,
  faScalpel,
  faBedPulse,
  faBullseyeArrow,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { CodeSetBlock } from 'src/components/Profiles/components/DetailBlocks/CodeSetBlock';
import { StandardBlock } from 'src/components/Profiles/components/DetailBlocks/StandardBlock';
import { Drawer } from 'src/components/PulseUI/Drawer';
import { CircularProgress } from 'src/components/PulseUI/CircularProgress';
import { Tabs } from 'src/components/PulseUI/Tabs';
import { TabSet } from 'src/components/PulseUI/Tabs/components/TabSet';
import { Tab } from 'src/components/PulseUI/Tabs/components/Tab';
import { TabPanel } from 'src/components/PulseUI/Tabs/components/TabPanel';
import { SitesBySpecialty } from 'src/components/Profiles/components/SitesBySpecialty';
import { ProfileAffiliations } from 'src/components/Profiles/components/ProfileAffiliations';
import { ProfileTreatingVolume } from 'src/components/Profiles/components/ProfileTreatingVolume';
import { ActivityList } from 'src/components/Activity/components/ActivityList';
import { ProfileCRMDetails } from 'src/components/Profiles/components/ProfileCRMDetails';
import { ProfilePublications } from 'src/components/Profiles/components/ProfilePublications';
import { NewProfileTopProviders } from 'src/components/Profiles/components/ProfileTopProviders';
import { ProfileReimbursement } from 'src/components/Profiles/components/ProfileReimbursement';
import { NewProfileVolumeByLocation } from 'src/components/Profiles/components/ProfileVolumeByLocation';
import { ProfileFilterBar } from 'src/components/Profiles/components/ProfileFilterBar';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import { useSite } from 'src/context/providers/SiteProvider';
import { OldProfileProvider } from 'src/context/providers/ProfileProvider';
import {
  useScoutContext,
  toScoutOpenProvider,
  SCOUT_CONTEXT_KEYS,
} from 'src/hooks/scout';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { truncateString } from 'src/utils/truncateString/truncateString';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';

// Components not exported from main src/components
import ProfileDetails from 'src/components/Profiles/components/ProfileDetails/ProfileDetails';
import ProfileCenterDetails from 'src/components/Profiles/components/ProfileDetails/ProfileCenterDetails';
import ProfileEducationDetails from 'src/components/Profiles/components/ProfileEducationDetails';
import ProfileProfessionalMix from 'src/components/Profiles/components/ProfileProfessionalMix/ProfileProfessionalMix';
import ProfileExecTeam from 'src/components/Profiles/components/ProfileExecTeam';
import ProfileSiteOfCareDetails from 'src/components/Profiles/components/ProfileDetails/ProfileSiteOfCareDetails';
import ProfileSystemDetails from 'src/components/Profiles/components/ProfileDetails/ProfileSystemDetails';
import { ProfileSiteOfCareMembers } from 'src/components/Profiles/components/SiteOfCareMembers';
import { NewPayerMix } from 'src/modules/Profile/components/Views/components/NewPayerMix';
import { ProfileDrawerHeader, StrategyProfileContent } from './components';
import {
  useGetProviderByIdQuery,
  useGetProviderStrategyTabsQuery,
  useGetActivityTypesQuery,
  useGetEntityActivitiesQuery,
} from 'src/api/redux';

const ProfileDrawer: React.FC = () => {
  const pathName = usePathname();
  const { state: siteState } = useSite();
  const providerId = siteState?.profile?.provider?.id;
  const drawerId = 'profile';
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const auth = useAuthorization();
  const { profileStrategies, useProfileTreatingVolume } = useFlags();
  const { selectedChainIds } = useStrategies(LOCATION.STRATEGY_CENTER);

  const [activity, setActivity] = React.useState<{
    label: string;
    value: string | null;
  }>({
    label: 'All Activity',
    value: null,
  });
  const [isActivityDropdownOpen, setIsActivityDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const activityDropdownRef = useRef<HTMLDivElement>(null);
  const activityButtonRef = useRef<HTMLButtonElement>(null);

  const isOpen = isDrawerOpen(drawerId);

  // Tell Scout which provider is open here (ID and type only).
  const providerType = siteState?.profile?.provider?.type;
  const scoutOpenProvider = useMemo(
    () => toScoutOpenProvider(isOpen, providerId, providerType),
    [isOpen, providerId, providerType]
  );
  useScoutContext(
    SCOUT_CONTEXT_KEYS.PROFILE_DRAWER,
    'Provider open in the profile drawer',
    scoutOpenProvider
  );
  const { data: activityTypes } = useGetActivityTypesQuery();

  const {
    currentData: entityDetails,
    isLoading,
    refetch,
  } = useGetProviderByIdQuery(
    {
      providerId: providerId,
    },
    {
      skip: !providerId,
    }
  );

  const { isLoading: loadingActivities } = useGetEntityActivitiesQuery(
    { providerId: entityDetails?.provider_id },
    { skip: !entityDetails?.provider_id }
  );

  // Fetch strategy chains for this provider (only when showing strategy tab)
  const { data: strategyTabsResponse, isLoading: isChainLoading } =
    useGetProviderStrategyTabsQuery(
      { providerId: entityDetails?.id || '' },
      {
        skip:
          !entityDetails?.id || !auth.hasPermission(TARGET_STRATEGIES_READ),
      }
    );

  // Extract all strategy tabs from API response
  const strategyTabs = React.useMemo(() => {
    if (!strategyTabsResponse?.results?.length) return [];

    return strategyTabsResponse.results.map((chain: any) => ({
      chainId: chain.id,
      id: chain.id, // Use chain.id to match against selectedStrategy.id (ChainedStrategy)
      targetStrategyId: chain.target_strategy.id,
      label: chain.label,
    }));
  }, [strategyTabsResponse]);

  // Find first selectedChainId that matches a strategy tab
  const selectedStrategyIndex = React.useMemo(() => {
    if (!selectedChainIds.length || !strategyTabs.length) return -1;

    for (const chainId of selectedChainIds) {
      const index = strategyTabs.findIndex((tab: any) => tab.id === chainId);
      if (index >= 0) return index;
    }

    return -1; // No match — will resolve to Overview tab
  }, [selectedChainIds, strategyTabs]);

  // Compute default tab index for PulseUI TabSet
  // Match → strategy tab; no match or none selected → Overview tab
  const defaultTabIndex = useMemo(() => {
    if (selectedStrategyIndex >= 0) {
      return selectedStrategyIndex;
    }
    return strategyTabs.length; // Overview tab
  }, [selectedStrategyIndex, strategyTabs.length]);

  // Tab scroll arrow state
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = tabScrollRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    );
  }, []);

  useEffect(() => {
    const container = tabScrollRef.current;
    const content = tabContentRef.current;
    if (!container) return;

    updateScrollState();

    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    // Observe the inner content for size changes (when tabs are added/removed)
    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    // Observe both container (for drawer resize) and content (for tab changes)
    resizeObserver.observe(container);
    if (content) {
      resizeObserver.observe(content);
    }

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [strategyTabs, isOpen]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const container = tabScrollRef.current;
    if (!container) return;

    const scrollAmount = 150;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const showLoading =
    !providerId || isLoading || loadingActivities || isChainLoading;

  const activityData = useMemo(() => {
    if (!activityTypes?.results) return [];
    return activityTypes.results.map((item) => ({
      ...item,
      label: item.label,
      value: String(item.id),
    }));
  }, [activityTypes]);

  const handleActivityChange = useCallback(
    (value: string | null) => {
      if (!value) {
        setActivity({
          label: 'All Activity',
          value: null,
        });
      } else {
        const newActivity = activityData?.find((item) => item.value === value);
        setActivity(newActivity || { label: 'All Activity', value: null });
      }
      setIsActivityDropdownOpen(false);
    },
    [activityData]
  );

  const toggleActivityDropdown = useCallback(() => {
    if (!isActivityDropdownOpen && activityButtonRef.current) {
      const rect = activityButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsActivityDropdownOpen((prev) => !prev);
  }, [isActivityDropdownOpen]);

  // Close activity dropdown when clicking outside
  useEffect(() => {
    if (!isActivityDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the dropdown portal
      if (activityDropdownRef.current?.contains(target)) {
        return;
      }

      // Check if click is on the toggle button
      if (activityButtonRef.current?.contains(target)) {
        return;
      }

      setIsActivityDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isActivityDropdownOpen]);

  const handleClose = useCallback(() => {
    closeDrawer(drawerId);
  }, [closeDrawer, drawerId]);

  const contentType = entityDetails?.type;

  const hasReimbursementPermission = useMemo(
    () => auth.hasPermission('show.reimbursement.profile'),
    [auth]
  );

  const showReimbursement = hasReimbursementPermission;

  const renderSharedComponents = (
    location: 'center' | 'hcp' | 'clinic' | 'system' | 'site_of_care'
  ) => {
    const isSystem = location === 'system';
    return (
      <>
        {!isSystem && (
          <>
            <ProfileFilterBar location={location} disableSticky inDrawer />
            {useProfileTreatingVolume && (
              <ProfileTreatingVolume location={location} />
            )}
            {contentType === 'hcp' && (
              <NewProfileVolumeByLocation location="hcp" />
            )}
            {contentType !== 'hcp' && (
              <NewProfileTopProviders location={location} />
            )}
            <NewPayerMix showLegend location={location} />
            {showReimbursement && <ProfileReimbursement location={location} compact />}
            <ProfileCRMDetails />
          </>
        )}
      </>
    );
  };

  // Type-specific additional components
  const renderTypeSpecificComponents = () => {
    switch (contentType) {
      case 'hcp':
        return (
          <>
            <ProfileDetails />
            <ProfileAffiliations />
            <ProfileEducationDetails />
            <ProfilePublications />
          </>
        );
      case 'center':
        return (
          <>
            <ProfileAffiliations />
            <ProfileExecTeam />
            <ProfileCenterDetails />
            <ProfileProfessionalMix />
          </>
        );
      case 'clinic':
        return (
          <>
            <ProfileAffiliations />
          </>
        );
      case 'site_of_care':
        return (
          <>
            <ProfileSiteOfCareDetails />
            <ProfileSiteOfCareMembers />
            <ProfileAffiliations />
            <ProfileExecTeam />
            <ProfileProfessionalMix />
          </>
        );
      case 'system':
        return (
          <div className="h-full w-full max-w-7xl mx-auto overflow-hidden">
            <div className="h-full space-y-4">
              {/* Detail blocks */}
              <div className="grid grid-cols-2 gap-4">
                {/* Code Set Block */}
                <CodeSetBlock />
                {/* Standard Blocks */}
                <StandardBlock
                  icon={faHospital}
                  title="Sites"
                  detail={(entityDetails?.total_sites || 0).toLocaleString()}
                />
                <StandardBlock
                  icon={faScalpel}
                  title="Operating Rooms"
                  detail={(
                    entityDetails?.operating_room_count || 0
                  ).toLocaleString()}
                />
                <StandardBlock
                  icon={faBedPulse}
                  title="Beds"
                  detail={(
                    entityDetails?.total_bed_count || 0
                  ).toLocaleString()}
                />
                <StandardBlock
                  icon={faCircleDollar}
                  title="Total Patient Revenue"
                  detail={formatCurrency(
                    entityDetails?.total_patient_revenue || 0
                  )}
                />
                <StandardBlock
                  icon={faBriefcaseMedical}
                  title="Current Assets"
                  detail={formatCurrency(
                    entityDetails?.total_current_assets || 0
                  )}
                />
                <StandardBlock
                  icon={faBriefcaseMedical}
                  title="Total Assets"
                  detail={formatCurrency(entityDetails?.total_assets || 0)}
                />
                <StandardBlock
                  icon={faBriefcaseMedical}
                  title="Total Liability"
                  detail={formatCurrency(
                    entityDetails?.total_current_liabilities || 0
                  )}
                />
              </div>

              {/* Payer Mix and Sites by Specialty */}
              <div className="flex flex-col gap-4">
                <NewPayerMix showLegend />
                <SitesBySpecialty systemId={entityDetails?.id} />
                <ProfileSystemDetails />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      variant="temporary"
      hideBackdrop
      clickOutside={true}
      anchor="right"
      size="lg"
    >
      <OldProfileProvider
        value={{
          entityDetails,
          refetch,
        }}
      >
        <ProfileDrawerHeader onClose={handleClose} />
        <div className="overflow-hidden flex-1 flex flex-col relative bg-neutral-50">
          {/* Loading overlay over content area */}
          {showLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
              <CircularProgress size="sm" color="neutral" />
            </div>
          )}

          <Tabs className="flex flex-col flex-1 overflow-hidden">
            <div className="flex flex-col items-start self-stretch border-t border-b border-neutral-300 bg-white px-2">
              <div className="relative flex w-full h-14 items-center">
                {/* Left scroll arrow */}
                {canScrollLeft && (
                  <button
                    onClick={() => scrollTabs('left')}
                    className="absolute left-0 z-10 flex items-center justify-center w-8 h-full bg-gradient-to-r from-white via-white to-transparent cursor-pointer"
                    aria-label="Scroll tabs left"
                  >
                    <Icon icon={faChevronLeft} className="text-neutral-500" />
                  </button>
                )}

                {/* Scrollable tabs container */}
                <div
                  ref={tabScrollRef}
                  className="flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                >
                  <div ref={tabContentRef} className="w-max">
                    <TabSet
                      id="profile-drawer-tabs"
                      defaultIndex={defaultTabIndex}
                      direction="horizontal"
                      className="flex w-max h-14 items-end gap-2"
                    >
                      {/* Strategy tabs */}
                      {strategyTabs.length > 0 &&
                        strategyTabs.map((strategy, index) => (
                          <Tab
                            key={`strategy-${strategy.chainId}`}
                            tabSetId="profile-drawer-tabs"
                            index={index}
                            startDecorator={
                              <Icon icon={faBullseyeArrow} size="sm" />
                            }
                            className="h-14 px-3 flex-shrink-0 min-w-28 flex items-center justify-center"
                          >
                            {truncateString(strategy.label, 25)}
                          </Tab>
                        ))}

                      {/* Overview Tab */}
                      <Tab
                        tabSetId="profile-drawer-tabs"
                        index={strategyTabs.length}
                        className="h-14 px-3 flex-shrink-0 min-w-28 flex items-center justify-center"
                      >
                        Overview
                      </Tab>

                      {/* Activity Tab */}
                      <Tab
                        tabSetId="profile-drawer-tabs"
                        index={strategyTabs.length + 1}
                        className="h-14 px-3 flex-shrink-0 min-w-28 flex items-center justify-center"
                      >
                        Activity
                      </Tab>
                    </TabSet>
                  </div>
                </div>

                {/* Right scroll arrow */}
                {canScrollRight && (
                  <button
                    onClick={() => scrollTabs('right')}
                    className="absolute right-0 z-10 flex items-center justify-center w-8 h-full bg-gradient-to-l from-white via-white to-transparent cursor-pointer"
                    aria-label="Scroll tabs right"
                  >
                    <Icon icon={faChevronRight} className="text-neutral-500" />
                  </button>
                )}
              </div>
            </div>

            {/* Strategy TabPanels */}
            {strategyTabs.length > 0 &&
              strategyTabs.map((strategy, index) => (
                <TabPanel
                  key={`strategy-panel-${strategy.chainId}`}
                  tabSetId="profile-drawer-tabs"
                  index={index}
                  className="relative flex flex-col items-center flex-1 self-stretch h-full overflow-y-auto scrollbar-none"
                >
                  <div className="flex p-4 flex-col gap-4 self-stretch flex-1 min-w-0 overflow-visible">
                    <StrategyProfileContent
                      showOverviewHeader={false}
                      chainId={strategy.chainId}
                      providerId={providerId}
                      providerType={contentType}
                    />
                  </div>
                </TabPanel>
              ))}

            {/* Overview TabPanel */}
            <TabPanel
              tabSetId="profile-drawer-tabs"
              index={strategyTabs.length}
              className="relative flex flex-col items-center flex-1 self-stretch h-full overflow-y-auto scrollbar-none"
            >
              <div className="flex p-4 flex-col gap-4 self-stretch flex-1 min-w-0 overflow-visible">
                {pathName &&
                  pathName.includes('strategy-center') &&
                  !profileStrategies && (
                    <StrategyProfileContent
                      providerId={providerId}
                      providerType={contentType}
                    />
                  )}

                {contentType === 'hcp' && (
                  <>
                    {renderSharedComponents('hcp')}
                    {renderTypeSpecificComponents()}
                  </>
                )}
                {contentType === 'center' && (
                  <>
                    {renderSharedComponents('center')}
                    {renderTypeSpecificComponents()}
                  </>
                )}
                {contentType === 'clinic' && (
                  <>
                    {renderSharedComponents('clinic')}
                    {renderTypeSpecificComponents()}
                  </>
                )}
                {contentType === 'system' && (
                  <>
                    {renderSharedComponents('system')}
                    {renderTypeSpecificComponents()}
                  </>
                )}
                {contentType === 'site_of_care' && (
                  <>
                    {renderSharedComponents('site_of_care')}
                    {renderTypeSpecificComponents()}
                  </>
                )}
              </div>
            </TabPanel>

            {/* Activity TabPanel */}
            <TabPanel
              tabSetId="profile-drawer-tabs"
              index={strategyTabs.length + 1}
              className="relative flex flex-col items-center flex-1 self-stretch h-full overflow-y-auto scrollbar-none"
            >
              <div className="sticky top-0 left-0 flex p-4 justify-between items-start self-stretch border-b border-neutral-200 bg-white z-10">
                <div className="relative">
                  <button
                    ref={activityButtonRef}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleActivityDropdown();
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="px-4 py-2 bg-white rounded-full border border-neutral-300 inline-flex justify-between items-center gap-2 min-w-[160px] hover:bg-neutral-50 cursor-pointer"
                  >
                    <span className="text-sm font-medium text-primary">
                      {activity.label}
                    </span>
                    <Icon
                      icon={faCaretDown}
                      className={`text-neutral-600 w-3 h-3 transition-transform duration-200 ${
                        isActivityDropdownOpen ? '-rotate-90' : 'rotate-0'
                      }`}
                    />
                  </button>
                  {isActivityDropdownOpen &&
                    dropdownPosition &&
                    typeof window !== 'undefined' &&
                    createPortal(
                      <div
                        ref={activityDropdownRef}
                        className="fixed w-auto min-w-[200px] bg-white rounded-lg shadow-lg border border-neutral-200 py-1 max-h-60 overflow-y-auto z-[99999]"
                        style={{
                          top: dropdownPosition.top,
                          left: dropdownPosition.left,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div
                          onClick={() => handleActivityChange(null)}
                          className={`px-3 py-2 hover:bg-neutral-50 cursor-pointer text-sm font-normal flex items-center ${
                            !activity.value
                              ? 'bg-sky-50 text-sky-700'
                              : 'text-text-secondary'
                          }`}
                        >
                          All Activity
                        </div>
                        {activityData.map((item) => (
                          <div
                            key={item.value}
                            onClick={() => handleActivityChange(item.value)}
                            className={`px-3 py-2 hover:bg-neutral-50 cursor-pointer text-sm font-normal flex items-center ${
                              activity.value === item.value
                                ? 'bg-sky-50 text-sky-700'
                                : 'text-text-secondary'
                            }`}
                          >
                            {item.label}
                          </div>
                        ))}
                      </div>,
                      document.body
                    )}
                </div>
              </div>
              <div className="flex p-4 flex-col gap-4 self-stretch flex-1 min-w-0">
                <ActivityList
                  isFiltered={!!activity.value}
                  activityTypeFilter={activity.value}
                />
              </div>
            </TabPanel>
          </Tabs>
        </div>
      </OldProfileProvider>
    </Drawer>
  );
};

export default ProfileDrawer;
